/**
 * Galaxy 턴 ↔ iPhone 텍스트 정렬 (TRANSCRIPT_PROTOCOL §6·§8).
 * - 기기 타임스탬프를 1:1로 비교하지 않는다: iPhone 전체를 하나의 토큰 흐름으로 펴고,
 *   Galaxy 턴마다 앞으로 나아가며 가장 비슷한 연속 구간을 찾는다 (단조 정렬 → drift 자동 흡수).
 * - iPhone의 긴 혼합 블록은 이렇게 Galaxy 턴 단위로 잘려 투영된다 (화자 라벨은 쓰지 않음).
 * - 맞는 구간이 없으면(한쪽 누락) 그 턴은 Galaxy 단독으로 남고 실패하지 않는다.
 */
import type { RawTurn } from './parse';
import { contentTokens, similarity } from './normalize';

export type StreamToken = { norm: string; block: number; start: number; end: number };

export type AlignedSpan = {
  /** iPhone 흐름 토큰 범위 [from, to) */
  from: number;
  to: number;
  score: number;
  text: string;
  startMs: number;
  endMs: number;
  refs: { block: number; charStart: number; charEnd: number }[];
};

export type Alignment = {
  perTurn: (AlignedSpan | null)[];
  /** Galaxy에 대응이 없는 iPhone 구간 (녹음 시작 전, Galaxy 누락 등) */
  iphoneOnly: AlignedSpan[];
  /** 추정 시간차: iPhone 시각 − Galaxy 시각 (ms) */
  offsetMs: number | null;
};

const ACCEPT = 0.45;
const WINDOW = 400;
/** iPhone 블록은 약 50초 단위라 블록 시작 시각 기준으로 넉넉히 본다 */
const TIME_SLACK_MS = 150_000;

export function buildStream(blocks: RawTurn[]): StreamToken[] {
  const out: StreamToken[] = [];
  blocks.forEach((b, i) => {
    for (const t of contentTokens(b.text)) out.push({ norm: t.norm, block: i, start: t.start, end: t.end });
  });
  return out;
}

function spanOf(stream: StreamToken[], blocks: RawTurn[], from: number, to: number, score: number): AlignedSpan {
  const refs: AlignedSpan['refs'] = [];
  for (let k = from; k < Math.min(to, stream.length); k++) {
    const t = stream[k]!;
    const last = refs.at(-1);
    if (last && last.block === t.block) last.charEnd = t.end;
    else refs.push({ block: t.block, charStart: t.start, charEnd: t.end });
  }
  const text = refs.map((r) => blocks[r.block]!.text.slice(r.charStart, r.charEnd)).join(' ');
  const firstBlock = blocks[refs[0]?.block ?? 0];
  const lastBlock = blocks[refs.at(-1)?.block ?? 0];
  return {
    from,
    to,
    score,
    text,
    startMs: firstBlock?.startMs ?? 0,
    endMs: blocks[(refs.at(-1)?.block ?? 0) + 1]?.startMs ?? lastBlock?.startMs ?? 0,
    refs,
  };
}

export function alignTranscripts(galaxy: RawTurn[], iphone: RawTurn[]): Alignment {
  const stream = buildStream(iphone);
  const norms = stream.map((t) => t.norm);
  const tokMs = stream.map((t) => iphone[t.block]!.startMs);
  const perTurn: (AlignedSpan | null)[] = [];
  let cursor = 0;
  const recentOffsets: number[] = [];
  const offsets: number[] = [];
  const median = (xs: number[]) => [...xs].sort((a, b) => a - b)[Math.floor(xs.length / 2)]!;

  const turnTokens = galaxy.map((t) => contentTokens(t.text).map((x) => x.norm));
  for (const [ti, turn] of galaxy.entries()) {
    const g = turnTokens[ti]!;
    // 뒤따르는 턴(문맥)도 함께 맞춰 본다: 같은 문장이 반복돼도 앞뒤 문맥이 다르면 구분된다
    const next = [
      ...(turnTokens[ti + 1] ?? []),
      ...((turnTokens[ti + 1]?.length ?? 0) < 4 ? (turnTokens[ti + 2] ?? []) : []),
    ].slice(0, 12);
    if (g.length < 2 || norms.length === 0) {
      perTurn.push(null);
      continue;
    }
    const n = g.length;
    // 기대 위치: 지금까지 추정한 기기 간 시간차로 iPhone 시각을 예상하고 그 주변만 본다 (반복 문장에 끌려가지 않게)
    const offset = recentOffsets.length ? median(recentOffsets) : null;
    const expected = offset === null ? null : turn.startMs + offset;
    const lo = Math.max(0, cursor - 30);
    const hi = offset === null ? Math.min(norms.length - 1, cursor + WINDOW * 3) : norms.length - 1;
    let best = { p: -1, len: n, s: 0, rank: -1 };
    for (let p = lo; p <= hi; p++) {
      if (expected !== null) {
        if (tokMs[p]! < expected - TIME_SLACK_MS) continue;
        if (tokMs[p]! > expected + TIME_SLACK_MS) break;
      }
      const s = similarity(g, norms.slice(p, p + n));
      if (s < ACCEPT) continue;
      const ctx = next.length ? similarity(next, norms.slice(p + n, p + n + next.length)) : 0;
      // 점수가 비슷하면 문맥이 맞고 커서(직전 정렬 위치)에 가까운 쪽을 고른다
      const rank = s + 0.6 * ctx - Math.abs(p - cursor) * 0.0005;
      if (rank > best.rank) best = { p, len: n, s, rank };
    }
    if (best.p < 0) {
      perTurn.push(null);
      continue;
    }
    // iPhone 전사가 먼저 끝난 경우(녹음을 먼저 멈춤 등): 맞춘 구간이 iPhone 전사 끝을 넘지 않게
    const room = norms.length - best.p;
    best.len = Math.min(best.len, room);
    for (const d of [-3, -2, -1, 1, 2, 3]) {
      const len = n + d;
      if (len < 1 || len > room) continue;
      const s = similarity(g, norms.slice(best.p, best.p + len));
      if (s > best.s + 0.01) best = { ...best, len, s };
    }
    const span = spanOf(stream, iphone, best.p, best.p + best.len, Number(best.s.toFixed(3)));
    perTurn.push(span);
    const off = tokMs[best.p]! - turn.startMs;
    offsets.push(off);
    recentOffsets.push(off);
    if (recentOffsets.length > 9) recentOffsets.shift();
    cursor = best.p + best.len;
  }

  // Galaxy에 대응이 없는 iPhone 구간
  const covered = new Array<boolean>(norms.length).fill(false);
  for (const s of perTurn) if (s) for (let k = s.from; k < s.to; k++) covered[k] = true;
  const iphoneOnly: AlignedSpan[] = [];
  let k = 0;
  while (k < norms.length) {
    if (covered[k]) {
      k++;
      continue;
    }
    let e = k;
    while (e < norms.length && !covered[e]) e++;
    if (e - k >= 6) iphoneOnly.push(spanOf(stream, iphone, k, e, 0));
    k = e;
  }

  offsets.sort((a, b) => a - b);
  return { perTurn, iphoneOnly, offsetMs: offsets.length ? offsets[Math.floor(offsets.length / 2)]! : null };
}
